import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../api.js';
import TeamBadge from './TeamBadge.js';
import styles from './TeamPicker.module.css';

export type TeamScope = 'all' | 'teams';

/**
 * "Which matches should this bolão include?" — the whole competition
 * (the only option before this existed), or only the ones involving a
 * hand-picked subset of teams.
 *
 * Fetches the competition's own teams via the same
 * `/football-competitions/:id/teams` lookup the custom-question authoring
 * flow already uses (`football-lookup.ts`) — real teams from real seeded
 * matches, never a separate catalog that could drift from what actually gets
 * played. Only queried once `scope` is `'teams'`: picking "entire
 * competition" needs no team list at all, so there's nothing to fetch for it.
 */
export default function TeamPicker({
  competitionId,
  locale,
  scope,
  onScopeChange,
  selectedTeamIds,
  onSelectedTeamIdsChange,
}: {
  competitionId: string;
  locale: string;
  scope: TeamScope;
  onScopeChange: (scope: TeamScope) => void;
  selectedTeamIds: string[];
  onSelectedTeamIdsChange: (teamIds: string[]) => void;
}) {
  const { t } = useTranslation();

  const { data, isPending, isError } = useQuery({
    queryKey: ['football-teams', competitionId, locale],
    queryFn: () => api.listFootballTeams(competitionId, locale),
    enabled: scope === 'teams' && competitionId.length > 0,
  });

  function toggleTeam(teamId: string) {
    onSelectedTeamIdsChange(
      selectedTeamIds.includes(teamId)
        ? selectedTeamIds.filter((id) => id !== teamId)
        : [...selectedTeamIds, teamId],
    );
  }

  return (
    <div>
      <span className="label">{t('createCompetition.teamsScopeLabel')}</span>
      <div
        className={styles.scopeGroup}
        role="radiogroup"
        aria-label={t('createCompetition.teamsScopeLabel')}
      >
        <button
          type="button"
          className={`${styles.scopeOption} ${scope === 'all' ? styles.scopeOptionActive : ''}`}
          aria-pressed={scope === 'all'}
          onClick={() => onScopeChange('all')}
        >
          {t('createCompetition.teamsScopeAll')}
        </button>
        <button
          type="button"
          className={`${styles.scopeOption} ${scope === 'teams' ? styles.scopeOptionActive : ''}`}
          aria-pressed={scope === 'teams'}
          onClick={() => onScopeChange('teams')}
        >
          {t('createCompetition.teamsScopeSpecific')}
        </button>
      </div>

      {scope === 'teams' ? (
        <div className={styles.teamsPanel}>
          <p className="muted" style={{ fontSize: '0.8rem', margin: '0 0 var(--space-2)' }}>
            {t('createCompetition.teamsPickHint')}
          </p>

          {isPending ? (
            <p className="muted">{t('common.loading')}</p>
          ) : isError ? (
            <p className="error">{t('common.somethingWentWrong')}</p>
          ) : data.teams.length === 0 ? (
            <p className="muted">{t('createCompetition.teamsEmpty')}</p>
          ) : (
            <ul className={styles.teamList}>
              {data.teams.map((team) => {
                const checked = selectedTeamIds.includes(team.id);
                return (
                  <li key={team.id}>
                    <label className={`${styles.teamRow} ${checked ? styles.teamRowChecked : ''}`}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleTeam(team.id)}
                      />
                      <TeamBadge team={team} />
                      <span className={styles.teamName}>{team.name}</span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}
